import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';

import {
  MarketplaceCardPhotosDocument,
  type MarketplaceCardPhotosQuery,
} from './__generated__/marketplace-card-photos.zeus';

const CARD_PHOTOS_CACHE_TIME_MS = 5 * 60_000;
const EMPTY_GALLERY_PHOTOS: GalleryPhoto[] = [];

type MarketplaceCardListingPhoto = NonNullable<
  MarketplaceCardPhotosQuery['marketplace']['listingDetails']
>['photos'][number];

const toGalleryPhoto = (photo: MarketplaceCardListingPhoto): GalleryPhoto => ({
  url: photo.mediumUrl,
  urlXs: photo.extraSmallUrl,
  urlSm: photo.smallUrl,
  urlLg: photo.largeUrl,
  urlXl: photo.extraLargeUrl,
  isFloorPlan: false,
});

type UseMarketplaceCardPhotosOptions = {
  addressId: string;
  enabled: boolean;
};

export const useMarketplaceCardPhotos = ({
  addressId,
  enabled,
}: UseMarketplaceCardPhotosOptions) => {
  return useQuery({
    queryKey: ['marketplace', 'card-photos', addressId],
    queryFn: async ({ signal }) => {
      const data = await requestZeusFromBrowser(
        MarketplaceCardPhotosDocument,
        { addressId },
        signal,
      );
      return data.marketplace.listingDetails?.photos.map(toGalleryPhoto) ?? EMPTY_GALLERY_PHOTOS;
    },
    enabled,
    meta: { captureErrorContext: 'MarketplaceCardPhotos' },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: CARD_PHOTOS_CACHE_TIME_MS,
    gcTime: CARD_PHOTOS_CACHE_TIME_MS,
  });
};
